import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Checkbox,
  FileInput,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Text,
} from "@mantine/core";
import { importPimSchema } from "../pimApi";

export default function ImportSchemaModal({ opened, onClose, targetTenantId, onImported }) {
  const { t } = useTranslation("pim");
  const [file, setFile] = useState(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleClose = () => {
    setFile(null);
    setReplaceExisting(false);
    setResult(null);
    setError(null);
    setImporting(false);
    onClose();
  };

  const handleImport = async () => {
    if (!file || !targetTenantId) return;
    setImporting(true);
    setError(null);
    try {
      const text = await file.text();
      const exportData = JSON.parse(text);
      const importResult = await importPimSchema(exportData, targetTenantId, replaceExisting);
      setResult(importResult);
      onImported?.();
    } catch (err) {
      setError(err.message || t("transfer.importFailed"));
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={handleClose} title={t("transfer.importSchema")} size="md">
      {result ? (
        <Stack>
          <Text>
            {t("transfer.importedSchema", { name: result.schemaName })}
            {result.replacedExisting && (
              <Badge ml="xs" color="orange" variant="light" size="sm">{t("transfer.replacedExisting")}</Badge>
            )}
          </Text>
          <Text size="sm" c="dimmed">
            {t("transfer.schemaCounts", {
              traits: result.traitsImported,
              attributes: result.attributeDefinitionsImported,
              allowedValues: result.allowedValuesImported,
              productTypes: result.productTypesImported,
              channels: result.channelsImported,
            })}
          </Text>
          {result.warnings?.length > 0 && (
            <Stack gap="xs">
              <Text size="sm" fw={600} c="orange">{t("transfer.warnings", { count: result.warnings.length })}</Text>
              <ScrollArea h={160}>
                <Stack gap={4}>
                  {result.warnings.map((w, i) => (
                    <Text key={i} size="xs" c="dimmed">• {w}</Text>
                  ))}
                </Stack>
              </ScrollArea>
            </Stack>
          )}
          <Group justify="flex-end">
            <Button onClick={handleClose}>{t("common.close")}</Button>
          </Group>
        </Stack>
      ) : (
        <Stack>
          <Text size="sm" c="dimmed">
            {t("transfer.schemaHint")}
          </Text>
          <FileInput
            value={file}
            onChange={setFile}
            label={t("transfer.schemaFile")}
            placeholder={t("transfer.selectFile")}
            accept="application/json,.json"
            clearable
          />
          <Checkbox
            checked={replaceExisting}
            onChange={(e) => setReplaceExisting(e.currentTarget.checked)}
            label={t("transfer.replaceSchema")}
            description={t("transfer.replaceSchemaHint")}
          />
          {error && <Text c="red" size="sm">{error}</Text>}
          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose}>{t("common.cancel")}</Button>
            <Button onClick={handleImport} disabled={!file || !targetTenantId} loading={importing}>
              {t("transfer.import")}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
